import { useEffect, useState } from "react";
import { adminApi, api, formatApiError } from "@/lib/api";
import { Check, X, Clock, Sparkles, Users, PartyPopper, CalendarDays, Download, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["pending", "confirmed", "cancelled"];

const TYPE_META = {
  table: { label: "Table", Icon: Users, color: "text-[var(--trio-primary)]" },
  vip: { label: "VIP Room", Icon: Sparkles, color: "text-[var(--trio-accent)]" },
  group: { label: "Group Party", Icon: PartyPopper, color: "text-[var(--trio-accent)]" },
  event: { label: "Event RSVP", Icon: CalendarDays, color: "text-[var(--trio-accent)]" },
};

export default function ReservationsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/reservations");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function updateStatus(id, status) {
    try {
      await adminApi.patch(`/admin/reservations/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  const filtered = items.filter(
    (r) =>
      (statusFilter === "all" || r.status === statusFilter) &&
      (typeFilter === "all" || (r.booking_type || "table") === typeFilter)
  );

  function exportCSV() {
    if (filtered.length === 0) {
      toast.error("Nothing to export with current filters.");
      return;
    }
    const headers = [
      "Date", "Time", "Name", "Party Size", "Type", "Group/Event",
      "Phone", "Email", "Occasion", "Notes", "Status", "Created"
    ];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = filtered.map((r) => [
      r.date, r.time || "", r.name, r.party_size, r.booking_type || "table",
      r.group_name || r.event_title || "",
      r.phone, r.email, r.occasion || "", r.notes || "", r.status, r.created_at,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(escape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trio-reservations-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} reservation${filtered.length === 1 ? "" : "s"}.`);
  }

  return (
    <div data-testid="reservations-list" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-3 flex-1">
        <div className="flex flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--trio-muted)] font-bold self-center mr-1">
            Status:
          </span>
          {["all", ...STATUSES].map((s) => (
            <FilterBtn
              key={s}
              active={statusFilter === s}
              label={s}
              count={s !== "all" ? items.filter((r) => r.status === s).length : null}
              onClick={() => setStatusFilter(s)}
              testid={`res-filter-${s}`}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--trio-muted)] font-bold self-center mr-1">
            Type:
          </span>
          {["all", "table", "group", "vip", "event"].map((t) => (
            <FilterBtn
              key={t}
              active={typeFilter === t}
              label={t}
              count={
                t !== "all"
                  ? items.filter((r) => (r.booking_type || "table") === t).length
                  : null
              }
              onClick={() => setTypeFilter(t)}
              testid={`res-type-filter-${t}`}
            />
          ))}
        </div>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          data-testid="res-add-btn"
          className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> New Reservation
        </button>
        <button
          onClick={exportCSV}
          data-testid="res-export-csv"
          className="inline-flex items-center gap-2 border border-[var(--trio-border)] hover:border-[var(--trio-primary)] hover:text-[var(--trio-primary)] px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV ({filtered.length})
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--trio-muted)]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-[var(--trio-muted)] py-12 text-center bg-[var(--trio-surface)]">
          No reservations.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const type = r.booking_type || "table";
            const meta = TYPE_META[type] || TYPE_META.table;
            const Icon = meta.Icon;
            return (
              <article
                key={r.id}
                data-testid={`reservation-row-${r.id}`}
                className="bg-[var(--trio-surface)] border border-[var(--trio-border)] p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
              >
                <div className="md:col-span-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                    <span className="text-[10px] tracking-[0.25em] uppercase font-bold">
                      {meta.label} · {r.party_size} guests
                    </span>
                  </div>
                  {r.group_name && (
                    <div className="text-xs text-[var(--trio-accent)] font-bold mb-1">
                      🎉 {r.group_name}
                    </div>
                  )}
                  {r.event_title && (
                    <div className="text-xs text-[var(--trio-accent)] font-bold mb-1">
                      → {r.event_title}
                    </div>
                  )}
                  <div className="font-display uppercase text-lg tracking-tight">
                    {r.name}
                  </div>
                  <div className="text-xs text-[var(--trio-muted)] mt-0.5">
                    <a href={`mailto:${r.email}`} className="hover:underline">
                      {r.email}
                    </a>
                    <span className="mx-1">·</span>
                    <a href={`tel:${r.phone}`} className="hover:underline">
                      {r.phone}
                    </a>
                  </div>
                </div>
                <div className="md:col-span-3 text-sm">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> {r.date}
                  </div>
                  {r.time && (
                    <div className="text-[var(--trio-muted)] pl-5">{r.time}</div>
                  )}
                  {r.occasion && (
                    <div className="text-xs text-[var(--trio-muted)] mt-1">
                      {r.occasion}
                    </div>
                  )}
                </div>
                <div className="md:col-span-3 text-sm text-[var(--trio-muted)]">
                  {r.notes || (
                    <span className="italic opacity-50">No notes</span>
                  )}
                </div>
                <div className="md:col-span-2 flex flex-col gap-2 items-end">
                  <StatusBadge status={r.status} />
                  {r.checked_in_at && (
                    <span data-testid={`res-checked-in-${r.id}`} className="ml-2 text-[10px] uppercase tracking-widest bg-green-600 text-white px-2 py-0.5">Checked In</span>
                  )}
                  <div className="flex gap-1">
                    <IconBtn
                      title="Confirm"
                      testid={`res-confirm-${r.id}`}
                      onClick={() => updateStatus(r.id, "confirmed")}
                    >
                      <Check className="w-4 h-4 text-emerald-700" />
                    </IconBtn>
                    <IconBtn
                      title="Cancel"
                      testid={`res-cancel-${r.id}`}
                      onClick={() => updateStatus(r.id, "cancelled")}
                    >
                      <X className="w-4 h-4 text-red-700" />
                    </IconBtn>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <AddReservationDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={async (payload) => {
          try {
            const { data: created } = await api.post("/reservations", payload);
            // Auto-confirm since admin is entering a known booking
            await adminApi.patch(`/admin/reservations/${created.id}`, { status: "confirmed" });
            toast.success("Reservation added and confirmed.");
            setAddOpen(false);
            load();
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
          }
        }}
      />
    </div>
  );
}

function AddReservationDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", party_size: "2",
    date: "", time: "", occasion: "", notes: "",
    booking_type: "table", group_name: "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    await onCreated({
      name: form.name,
      email: form.email || null,
      phone: form.phone,
      party_size: parseInt(form.party_size, 10) || 1,
      date: form.date,
      time: form.time || null,
      occasion: form.occasion || null,
      notes: form.notes || null,
      booking_type: form.booking_type,
      group_name: form.booking_type === "group" ? form.group_name || null : null,
    });
    setSaving(false);
    setForm({ name: "", email: "", phone: "", party_size: "2", date: "", time: "", occasion: "", notes: "", booking_type: "table", group_name: "" });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="add-reservation-dialog"
        className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
            Manual Entry
          </div>
          <DialogTitle className="font-display uppercase text-2xl tracking-tight">
            New Reservation
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--trio-muted)]">
            For phone-ins, walk-ups, and verbal bookings. Automatically saved as confirmed.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs tracking-widest uppercase">Type</Label>
            <Select value={form.booking_type} onValueChange={(v) => update("booking_type", v)}>
              <SelectTrigger data-testid="add-res-type" className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none">
                <SelectItem value="table">Table</SelectItem>
                <SelectItem value="group">Group / Party</SelectItem>
                <SelectItem value="vip">VIP Room</SelectItem>
                <SelectItem value="event">Event</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.booking_type === "group" && (
            <Row label="Group / Celebrant name" value={form.group_name} onChange={(v) => update("group_name", v)} testid="add-res-group" placeholder="e.g. Jasmine's 30th" />
          )}
          <Row label="Guest name" value={form.name} onChange={(v) => update("name", v)} required testid="add-res-name" />
          <div className="grid grid-cols-2 gap-3">
            <Row label="Phone" value={form.phone} onChange={(v) => update("phone", v)} required testid="add-res-phone" />
            <Row label="Email (optional)" type="email" value={form.email} onChange={(v) => update("email", v)} testid="add-res-email" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Row label="Date" type="date" value={form.date} onChange={(v) => update("date", v)} required testid="add-res-date" />
            <Row label="Time" value={form.time} onChange={(v) => update("time", v)} placeholder="7:30 PM" testid="add-res-time" />
            <Row label="Party size" type="number" value={form.party_size} onChange={(v) => update("party_size", v)} required testid="add-res-size" />
          </div>
          <Row label="Occasion (optional)" value={form.occasion} onChange={(v) => update("occasion", v)} testid="add-res-occ" />
          <div>
            <Label className="text-xs tracking-widest uppercase">Notes</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              data-testid="add-res-notes"
              placeholder="Booking taken by, allergies, seating preferences…"
              className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="add-res-submit"
            className="w-full bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-3 text-xs tracking-widest uppercase font-bold disabled:opacity-60 transition-colors"
          >
            {saving ? "Saving…" : "Save Reservation"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs tracking-widest uppercase">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        min={type === "number" ? 1 : undefined}
        className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
      />
    </div>
  );
}

function FilterBtn({ active, label, count, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
        active
          ? "bg-[var(--trio-text)] text-[var(--trio-bg)] border-[var(--trio-text)]"
          : "border-[var(--trio-border)] hover:border-[var(--trio-text)]"
      }`}
    >
      {label} {count != null && `(${count})`}
    </button>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-100 text-amber-900 border-amber-300",
    confirmed: "bg-emerald-100 text-emerald-900 border-emerald-300",
    cancelled: "bg-red-100 text-red-900 border-red-300",
  };
  return (
    <span
      className={`text-[10px] tracking-[0.25em] uppercase font-bold border px-2 py-1 ${
        styles[status] || ""
      }`}
    >
      {status}
    </span>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="p-2 border border-[var(--trio-border)] hover:bg-[var(--trio-bg)] transition-colors"
    >
      {children}
    </button>
  );
}
